import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Select, Button, Card, Row, Col, Space, Divider, Switch, Upload, message, Modal, Alert, Radio, Typography, Badge, Steps, Descriptions, Tag, Popover } from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined, SendOutlined, SaveOutlined, UploadOutlined, FileImageOutlined, PlaySquareOutlined, FileTextOutlined, SmileOutlined, LoadingOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import EmojiPicker from 'emoji-picker-react';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { useMyContext } from 'Context/MyContextProvider';
import { useTemplates } from 'hooks/api';
import apiClient from 'services/apiClient';
import { listLocalMetaFlows, getMetaFlow } from 'services/metaFlowService';
import { PRIMARY, ErrorExtractor } from '../CustomUtils.js/utils';

const { Option } = Select;
const { Title, Text, Paragraph } = Typography;

const NewTemplate = () => {
    const { navigate, api } = useMyContext();
    const { templates = [] } = useTemplates();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [templateData, setTemplateData] = useState({});
    const [currentStep, setCurrentStep] = useState(0);
    const [components, setComponents] = useState([]);
    const [flows, setFlows] = useState([]);
    const [flowsLoading, setFlowsLoading] = useState(false);
    const [flowScreensById, setFlowScreensById] = useState({});
    const [flowScreensLoadingId, setFlowScreensLoadingId] = useState(null);
    const [buttons, setButtons] = useState([]);
    const [headerType, setHeaderType] = useState('TEXT');
    const [headerHandle, setHeaderHandle] = useState(null);
    const [uploadDisabled, setUploadDisabled] = useState(false);
    const [selectedMediaType, setSelectedMediaType] = useState(null);
    const [submitError, setSubmitError] = useState(null);
    const bodyTextAreaRef = useRef(null);

    useEffect(() => {
        setSubmitError(null);
    }, [currentStep]);

    useEffect(() => {
        const fetchFlows = async () => {
            setFlowsLoading(true);
            try {
                const response = await listLocalMetaFlows();
                const flowsList = (response?.data || []).filter(
                    (flow) => String(flow.status || '').toUpperCase() === 'PUBLISHED'
                );
                setFlows(flowsList);
            } catch (error) {
                message.error('Failed to fetch published MetaFlows. Please try again later.');
            } finally {
                setFlowsLoading(false);
            }
        };

        fetchFlows();
    }, []);
    // Meta's button validation rules
    const BUTTON_LIMITS = {
        QUICK_REPLY: 3,      // Max 3 quick reply buttons
        URL: 2,              // Max 2 URL buttons  
        PHONE_NUMBER: 1,     // Max 1 phone button
        COPY_CODE: 1,        // Max 1 copy code button
        FLOW: 1,             // Max 1 flow button
        CATALOG: 1,          // Max 1 catalog button
        MPM: 1,              // Max 1 MPM button
        TOTAL: 10            // Max 10 total buttons
    };

    // Sample values for template examples (Meta wants realistic examples)
    const SAMPLE_VALUES = {
        text: ['John Doe', 'Order #12345', 'December 25, 2024', 'Premium Plan', '$99.99', 'ABC123'],
        currency: ['$99.99', '€49.95', '₹1299', '$149.00', '£79.99', '$29.99'],
        date_time: ['Dec 25, 2024', '2024-12-25 10:30 AM', 'Tomorrow at 3 PM', 'Next Monday', 'Jan 1, 2025', 'Tonight at 8 PM'],
        url_examples: ['product-123', 'user-456', 'order-789', 'category-abc', 'discount-50', 'promo-xyz']
    };

    // Template Categories as per Meta guidelines
    const templateCategories = [
        { value: 'AUTHENTICATION', label: 'Authentication', description: 'For sending login codes, verification codes' },
        { value: 'MARKETING', label: 'Marketing', description: 'For promotional content, announcements' },
        { value: 'UTILITY', label: 'Utility', description: 'For transactional updates, confirmations' }
    ];

    // Language codes supported by WhatsApp Business API
    const supportedLanguages = [
        { code: 'af', name: 'Afrikaans' },
        { code: 'sq', name: 'Albanian' },
        { code: 'ar', name: 'Arabic' },
        { code: 'az', name: 'Azerbaijani' },
        { code: 'bn', name: 'Bengali' },
        { code: 'bg', name: 'Bulgarian' },
        { code: 'ca', name: 'Catalan' },
        { code: 'zh_CN', name: 'Chinese (CHN)' },
        { code: 'zh_HK', name: 'Chinese (HKG)' },
        { code: 'zh_TW', name: 'Chinese (TAI)' },
        { code: 'hr', name: 'Croatian' },
        { code: 'cs', name: 'Czech' },
        { code: 'da', name: 'Danish' },
        { code: 'nl', name: 'Dutch' },
        { code: 'en', name: 'English' },
        { code: 'en_GB', name: 'English (UK)' },
        { code: 'en_US', name: 'English (US)' },
        { code: 'et', name: 'Estonian' },
        { code: 'fil', name: 'Filipino' },
        { code: 'fi', name: 'Finnish' },
        { code: 'fr', name: 'French' },
        { code: 'ka', name: 'Georgian' },
        { code: 'de', name: 'German' },
        { code: 'el', name: 'Greek' },
        { code: 'gu', name: 'Gujarati' },
        { code: 'ha', name: 'Hausa' },
        { code: 'he', name: 'Hebrew' },
        { code: 'hi', name: 'Hindi' },
        { code: 'hu', name: 'Hungarian' },
        { code: 'id', name: 'Indonesian' },
        { code: 'ga', name: 'Irish' },
        { code: 'it', name: 'Italian' },
        { code: 'ja', name: 'Japanese' },
        { code: 'kn', name: 'Kannada' },
        { code: 'kk', name: 'Kazakh' },
        { code: 'rw_RW', name: 'Kinyarwanda' },
        { code: 'ko', name: 'Korean' },
        { code: 'ky_KG', name: 'Kyrgyz (Kyrgyzstan)' },
        { code: 'lo', name: 'Lao' },
        { code: 'lv', name: 'Latvian' },
        { code: 'lt', name: 'Lithuanian' },
        { code: 'mk', name: 'Macedonian' },
        { code: 'ms', name: 'Malay' },
        { code: 'ml', name: 'Malayalam' },
        { code: 'mr', name: 'Marathi' },
        { code: 'nb', name: 'Norwegian' },
        { code: 'fa', name: 'Persian' },
        { code: 'pl', name: 'Polish' },
        { code: 'pt_BR', name: 'Portuguese (BR)' },
        { code: 'pt_PT', name: 'Portuguese (POR)' },
        { code: 'pa', name: 'Punjabi' },
        { code: 'ro', name: 'Romanian' },
        { code: 'ru', name: 'Russian' },
        { code: 'sr', name: 'Serbian' },
        { code: 'sk', name: 'Slovak' },
        { code: 'sl', name: 'Slovenian' },
        { code: 'es', name: 'Spanish' },
        { code: 'es_AR', name: 'Spanish (ARG)' },
        { code: 'es_ES', name: 'Spanish (SPA)' },
        { code: 'es_MX', name: 'Spanish (MEX)' },
        { code: 'sw', name: 'Swahili' },
        { code: 'sv', name: 'Swedish' },
        { code: 'ta', name: 'Tamil' },
        { code: 'te', name: 'Telugu' },
        { code: 'th', name: 'Thai' },
        { code: 'tr', name: 'Turkish' },
        { code: 'uk', name: 'Ukrainian' },
        { code: 'ur', name: 'Urdu' },
        { code: 'uz', name: 'Uzbek' },
        { code: 'vi', name: 'Vietnamese' },
        { code: 'zu', name: 'Zulu' }
    ];

    // Component types
    const componentTypes = [
        { value: 'HEADER', label: 'Header' },
        { value: 'BODY', label: 'Body' },
        { value: 'FOOTER', label: 'Footer' },
        { value: 'BUTTONS', label: 'Buttons' }
    ];

    // Header format types
    const headerFormats = [
        { value: 'TEXT', label: 'Text' },
        { value: 'MEDIA', label: 'Media' },
        { value: 'LOCATION', label: 'Location' }
    ];

    // Button types
    const buttonTypes = [
        { value: 'QUICK_REPLY', label: 'Quick Reply' },
        { value: 'PHONE_NUMBER', label: 'Phone Number' },
        { value: 'URL', label: 'URL' },
        { value: 'COPY_CODE', label: 'Copy Code' },
        { value: 'MPM', label: 'Multi-Product Message' },
        { value: 'CATALOG', label: 'Catalog' },
        { value: 'FLOW', label: 'Flow' }
    ];

    // Flow actions
    const flowActions = [
        { value: 'navigate', label: 'Navigate' },
        { value: 'data_exchange', label: 'Data Exchange' }
    ];

    useEffect(() => {
        // Initialize form with default values
        const defaultValues = {
            category: 'UTILITY',
            language: 'en_US',
            allow_category_change: false
        };

        // Multiple attempts to ensure form gets initialized
        const initializeForm = () => {
            try {
                form.setFieldsValue(defaultValues);
            } catch (error) {
                console.error('Error setting form values:', error);
            }
        };

        // Initialize immediately
        initializeForm();

        // Also initialize after a small delay as backup
        const timeoutId = setTimeout(initializeForm, 100);

        return () => clearTimeout(timeoutId);
    }, [form]);

    // Extract variables from text ({{1}}, {{2}}, etc.)
    const extractVariables = (text) => {
        if (!text) return [];
        const variablePattern = /{{\d+}}/g;
        const matches = text.match(variablePattern) || [];
        return matches.map(match => parseInt(match.replace(/[{}]/g, '')));
    };

    // Auto-reindex variables to be sequential starting from 1
    // e.g. if user deletes {{2}} from "Hello {{1}} {{2}} {{3}}", 
    // it becomes "Hello {{1}} {{2}}" ({{3}} is renumbered to {{2}})
    const reindexVariables = (text) => {
        if (!text) return text;
        const variables = extractVariables(text);
        if (variables.length === 0) return text;

        // Don't reindex if there are incomplete/broken variable patterns
        // (user is mid-edit, e.g. deleting characters from {{4}})
        const openBraces = (text.match(/{{/g) || []).length;
        const completedVars = (text.match(/{{\d+}}/g) || []).length;
        if (openBraces !== completedVars) return text;

        // Get unique variable numbers in the order they appear
        const seen = new Set();
        const orderedUnique = [];
        const variablePattern = /{{(\d+)}}/g;
        let match;
        while ((match = variablePattern.exec(text)) !== null) {
            const num = parseInt(match[1]);
            if (!seen.has(num)) {
                seen.add(num);
                orderedUnique.push(num);
            }
        }

        // Check if reindexing is needed
        const isAlreadySequential = orderedUnique.every((num, idx) => num === idx + 1);
        if (isAlreadySequential) return text;

        // Build a mapping: old number -> new sequential number (based on appearance order)
        const mapping = {};
        orderedUnique.forEach((oldNum, idx) => {
            mapping[oldNum] = idx + 1;
        });

        // Replace all occurrences using the mapping
        const reindexed = text.replace(/{{(\d+)}}/g, (fullMatch, numStr) => {
            const oldNum = parseInt(numStr);
            const newNum = mapping[oldNum];
            return newNum !== undefined ? `{{${newNum}}}` : fullMatch;
        });

        return reindexed;
    };

    // Validate variable sequence (must be sequential starting from 1)
    const validateVariableSequence = (text) => {
        const variables = extractVariables(text);
        if (variables.length === 0) return true;

        const sortedVars = [...variables].sort((a, b) => a - b);
        for (let i = 0; i < sortedVars.length; i++) {
            if (sortedVars[i] !== i + 1) return false;
        }
        return true;
    };

    // Add variable helper function — inserts at cursor position if ref is provided
    // After insertion, reindexes all variables by order of appearance
    const addVariable = (text, setText, textAreaRef) => {
        const existingVariables = extractVariables(text);
        const nextNumber = existingVariables.length === 0
            ? 1
            : Math.max(...existingVariables) + 1;
        const newVariable = `{{${nextNumber}}}`;

        // Try to get cursor position from the textarea ref
        const textAreaEl = textAreaRef?.current?.resizableTextArea?.textArea;
        if (textAreaEl && text) {
            const cursorPos = textAreaEl.selectionStart;
            const before = text.substring(0, cursorPos);
            const after = text.substring(cursorPos);
            const insertedText = `${before}${newVariable}${after}`;
            // Reindex so variables are sequential by appearance order
            const updatedText = reindexVariables(insertedText);
            setText(updatedText);

            // Restore cursor position after the inserted variable
            setTimeout(() => {
                const newCursorPos = cursorPos + newVariable.length;
                textAreaEl.focus();
                textAreaEl.setSelectionRange(newCursorPos, newCursorPos);
            }, 0);
        } else {
            const insertedText = text ? `${text} ${newVariable}` : newVariable;
            const updatedText = reindexVariables(insertedText);
            setText(updatedText);
        }
    };

    // Handle emoji selection
    const handleEmojiSelect = (emojiObject, componentId, currentText) => {
        const updatedText = currentText ? `${currentText}${emojiObject.emoji}` : emojiObject.emoji;
        updateComponent(componentId, 'text', updatedText);
        // The popover will close automatically after selection
    };

    // Generate realistic example values for variables
    const generateExampleValues = (variables, type = 'text') => {
        return variables.map((variable, index) => {
            const sampleArray = SAMPLE_VALUES[type] || SAMPLE_VALUES.text;
            return sampleArray[index % sampleArray.length];
        });
    };

    // Validate file for upload
    const validateMedia = (file) => {
        const maxSize = {
            'image/jpeg': 5 * 1024 * 1024, // 5MB for images
            'image/png': 5 * 1024 * 1024,  // 5MB for PNG
            'video/mp4': 16 * 1024 * 1024, // 16MB for mp4 videos
            'video/3gpp': 16 * 1024 * 1024, // 16MB for 3gp videos
            'application/pdf': 100 * 1024 * 1024 // 100MB for PDFs
        };

        const supportedFormats = [
            'image/jpeg', 'image/png', 'video/mp4', 'video/3gpp', 'application/pdf'
        ];

        if (!supportedFormats.includes(file.type)) {
            return {
                valid: false,
                message: 'Unsupported file format. Allowed: JPG, PNG, MP4, 3GP, PDF'
            };
        }

        if (file.size > maxSize[file.type]) {
            const maxSizeMB = maxSize[file.type] / (1024 * 1024);
            return {
                valid: false,
                message: `File too large. Max size for ${file.type} is ${maxSizeMB}MB`
            };
        }

        return { valid: true };
    };

    // Mock upload function (replace with your actual upload API)
    // const uploadFile = async (file) => {
    //     setLoading(true);
    //     try {
    //         const validation = validateMedia(file);
    //         if (!validation.valid) {
    //             message.error(validation.message);
    //             return;
    //         }

    //         // Mock upload - replace with your actual upload logic
    //         await new Promise(resolve => setTimeout(resolve, 2000));

    //         const mockHandle = `mock_handle_${Date.now()}`;
    //         setHeaderHandle(mockHandle);
    //         setUploadDisabled(true);
    //         message.success('Media uploaded successfully!');

    //         return mockHandle;
    //     } catch (error) {
    //         message.error('Upload failed: ' + error.message);
    //     } finally {
    //         setLoading(false);
    //     }
    // };
    const uploadFile = async (media) => {
        if (media) {
            setLoading(true)
            const validation = validateMedia(media);
            if (!validation.valid) {
                message.error(validation.message); // Display error message if validation fails
                return;
            }
            const formData = new FormData();
            formData.append('media', media);

            try {
                const response = await apiClient.post('upload-session', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                message.success('Header Set Successfully')
                // setUploadDisabled(true)
                setHeaderHandle(response.data);
                // Handle the response as needed
            } catch (error) {
                //  console.error('Error uploading file:', error);
            }
            finally {
                setLoading(false)
            }
        } else {
            // console.log('No file selected');
        }
    };
    const parseFlowScreens = (flowJson) => {
        if (!flowJson) return [];
        try {
            const parsed = typeof flowJson === 'string' ? JSON.parse(flowJson) : flowJson;
            return (parsed?.screens || [])
                .map((screen) => ({
                    id: String(screen.id || '').toUpperCase(),
                    title: screen.title || screen.id,
                }))
                .filter((screen) => screen.id);
        } catch {
            return [];
        }
    };

    const loadFlowScreens = async (flowId) => {
        if (!flowId || flowScreensById[flowId]) {
            return flowScreensById[flowId] || [];
        }

        setFlowScreensLoadingId(flowId);
        try {
            const data = await getMetaFlow(flowId);
            const screens = parseFlowScreens(data?.flow_json);
            setFlowScreensById((prev) => ({ ...prev, [flowId]: screens }));
            return screens;
        } catch (error) {
            message.error('Failed to load flow screens');
            return [];
        } finally {
            setFlowScreensLoadingId(null);
        }
    };

    // Validate button counts according to Meta's limits
    const validateButtonCounts = () => {
        const buttonCounts = buttons.reduce((counts, button) => {
            counts[button.type] = (counts[button.type] || 0) + 1;
            counts.TOTAL = (counts.TOTAL || 0) + 1;
            return counts;
        }, {});

        const errors = [];

        Object.entries(BUTTON_LIMITS).forEach(([type, limit]) => {
            if (buttonCounts[type] > limit) {
                if (type === 'TOTAL') {
                    errors.push(`Maximum ${limit} buttons allowed in total`);
                } else {
                    errors.push(`Maximum ${limit} ${type.replace('_', ' ').toLowerCase()} button(s) allowed`);
                }
            }
        });

        // Validate URL variables (max 1 per URL button)
        buttons.forEach((button, index) => {
            if (button.type === 'URL') {
                const urlVariables = extractVariables(button.url || '');
                if (urlVariables.length > 1) {
                    errors.push(`URL Button ${index + 1}: Only 1 variable allowed per URL button (currently has ${urlVariables.length})`);
                }
            }

            if (button.type === 'FLOW') {
                if (!button.text?.trim()) {
                    errors.push(`Flow Button ${index + 1}: Button text is required`);
                }
                if (!button.flow_id) {
                    errors.push(`Flow Button ${index + 1}: Select a published MetaFlow`);
                }
                if (!button.flow_action) {
                    errors.push(`Flow Button ${index + 1}: Flow action is required`);
                }
                if (button.flow_action === 'navigate' && !button.navigate_screen) {
                    errors.push(`Flow Button ${index + 1}: Navigate screen is required for navigate action`);
                }
            }
        });

        return errors;
    };

    // Get current button counts
    const getButtonCounts = () => {
        return buttons.reduce((counts, button) => {
            counts[button.type] = (counts[button.type] || 0) + 1;
            counts.TOTAL = (counts.TOTAL || 0) + 1;
            return counts;
        }, {});
    };

    // Check if a button type can be selected (not at limit)
    const canSelectButtonType = (buttonType, currentButtonId = null) => {
        const currentCounts = getButtonCounts();

        // If this is the current button's type, allow it (editing existing button)
        const currentButton = buttons.find(btn => btn.id === currentButtonId);
        if (currentButton && currentButton.type === buttonType) {
            return true;
        }

        // Check if adding this type would exceed the limit
        const currentCount = currentCounts[buttonType] || 0;
        const limit = BUTTON_LIMITS[buttonType];

        return currentCount < limit;
    };

    // Get button count summary for display
    const getButtonCountSummary = () => {
        const counts = getButtonCounts();
        const summary = [];

        Object.entries(BUTTON_LIMITS).forEach(([type, limit]) => {
            if (type !== 'TOTAL') {
                const current = counts[type] || 0;
                if (current > 0) {
                    const typeName = type.replace('_', ' ').toLowerCase();
                    summary.push(`${current}/${limit} ${typeName}`);
                }
            }
        });

        return summary;
    };

    // Add variable to URL with validation (max 1 variable for URL buttons)
    const addVariableToUrl = (currentUrl, buttonId) => {
        const existingVariables = extractVariables(currentUrl || '');

        // URL buttons can only have 1 variable maximum
        if (existingVariables.length >= 1) {
            message.warning('URL buttons can only contain 1 variable maximum');
            return;
        }

        const newVariable = '{{1}}';
        const updatedUrl = currentUrl ? `${currentUrl}${newVariable}` : newVariable;
        updateButton(buttonId, 'url', updatedUrl);
    };

    // Validate URL variables (max 1 for URL buttons)
    const validateUrlVariables = (url) => {
        const variables = extractVariables(url || '');
        return variables.length <= 1;
    };

    // Update variables when body text changes (with auto-reindex)
    const handleBodyChange = (componentId, text) => {
        const reindexedText = reindexVariables(text);
        updateComponent(componentId, 'text', reindexedText);
    };

    // Update variables when header text changes (with auto-reindex)
    const handleHeaderChange = (componentId, text) => {
        const reindexedText = reindexVariables(text);
        updateComponent(componentId, 'text', reindexedText);
    };

    const addComponent = (type) => {
        const newComponent = {
            id: Date.now(),
            type: type,
            format: type === 'HEADER' ? 'TEXT' : undefined,
            text: '',
            parameters: [],
            example: type === 'HEADER' && headerType !== 'TEXT' ? { header_handle: [] } : undefined
        };

        setComponents([...components, newComponent]);
    };

    const removeComponent = (id) => {
        setComponents(components.filter(comp => comp.id !== id));
    };

    const updateComponent = (id, field, value) => {
        setComponents(components.map(comp =>
            comp.id === id ? { ...comp, [field]: value } : comp
        ));
    };

    const addButton = () => {
        const newButton = {
            id: Date.now(),
            type: 'QUICK_REPLY',
            text: '',
            url: '',
            phone_number: '',
            copy_code_example: '',
            flow_id: '',
            flow_action: 'navigate',
            navigate_screen: '',
        };
        setButtons([...buttons, newButton]);
    };

    const removeButton = (id) => {
        setButtons(buttons.filter(btn => btn.id !== id));
    };

    const updateButton = (id, field, value) => {
        setButtons(buttons.map(btn =>
            btn.id === id ? { ...btn, [field]: value } : btn
        ));
    };

    const updateButtonFields = (id, fields) => {
        setButtons((prev) =>
            prev.map((btn) => (btn.id === id ? { ...btn, ...fields } : btn))
        );
    };

    const handleFlowSelect = async (buttonId, flowId) => {
        const selectedFlow = flows.find(
            (flow) => String(flow.meta_flow_id) === String(flowId)
        );
        const isDynamic = Boolean(selectedFlow?.is_dynamic);
        const defaultAction = isDynamic ? 'data_exchange' : 'navigate';

        updateButtonFields(buttonId, {
            flow_id: flowId,
            flow_action: defaultAction,
            navigate_screen: '',
        });

        if (defaultAction === 'navigate') {
            const screens = await loadFlowScreens(flowId);
            if (screens.length > 0) {
                updateButtonFields(buttonId, { navigate_screen: screens[0].id });
            }
        }
    };

    const handleFlowActionChange = async (buttonId, flowAction, flowId) => {
        updateButtonFields(buttonId, {
            flow_action: flowAction,
            navigate_screen: '',
        });

        if (flowAction === 'navigate' && flowId) {
            const screens = await loadFlowScreens(flowId);
            if (screens.length > 0) {
                updateButtonFields(buttonId, { navigate_screen: screens[0].id });
            }
        }
    };

    const handleButtonTypeChange = (buttonId, type) => {
        const fields = { type };
        if (type === 'FLOW') {
            fields.flow_id = '';
            fields.flow_action = 'navigate';
            fields.navigate_screen = '';
        }
        updateButtonFields(buttonId, fields);
    };

    const buildComponents = () => {
        const builtComponents = [];

        components.forEach(comp => {
            const component = {
                type: comp.type
            };

            if (comp.type === 'HEADER') {
                // Include ALL header types in components array
                if (comp.format === 'TEXT') {
                    component.format = 'TEXT';
                    component.text = comp.text;
                    // TEXT headers don't need example field
                } else if (comp.format === 'MEDIA') {
                    // For media headers, use the specific media type that Meta accepts
                    if (selectedMediaType === 'IMAGE') {
                        component.format = 'IMAGE';
                    } else if (selectedMediaType === 'VIDEO') {
                        component.format = 'VIDEO';
                    } else if (selectedMediaType === 'DOCUMENT') {
                        component.format = 'DOCUMENT';
                    } else {
                        // Default fallback
                        component.format = 'IMAGE';
                    }

                    // MEDIA headers REQUIRE example.header_handle
                    component.example = {
                        header_handle: [headerHandle['h'] || 'mock_media_handle']
                    };
                } else if (comp.format === 'LOCATION') {
                    component.format = 'LOCATION';
                    // Meta template creation API does not accept latitude/longitude, name, or address.
                    // Those parameters are only supplied when sending the actual message template.
                }

                // Add ALL header types to components
                builtComponents.push(component);
            } else if (comp.type === 'BODY') {
                component.text = comp.text;
                // Extract variables and generate examples with static values
                const variables = extractVariables(comp.text);
                if (variables.length > 0) {
                    // Generate static example values like "item1", "item2" for Meta submission
                    const staticExamples = variables.map((_, index) => `item${index + 1}`);
                    component.example = {
                        body_text: [staticExamples]
                    };
                }
                builtComponents.push(component);
            } else if (comp.type === 'FOOTER') {
                component.text = comp.text;
                builtComponents.push(component);
            }
        });

        if (buttons.length > 0) {
            const buttonComponent = {
                type: 'BUTTONS',
                buttons: buttons.map(btn => {
                    const button = {
                        type: btn.type,
                        text: btn.text
                    };

                    switch (btn.type) {
                        case 'PHONE_NUMBER':
                            button.phone_number = btn.phone_number;
                            break;
                        case 'URL':
                            button.url = btn.url;
                            // Handle dynamic URLs with examples
                            if (btn.url_type === 'DYNAMIC') {
                                const variables = extractVariables(btn.url);
                                if (variables.length > 0) {
                                    const urlExamples = generateExampleValues(variables, 'url_examples');
                                    button.example = urlExamples;
                                }
                            }
                            break;
                        case 'COPY_CODE':
                            if (btn.copy_code_example) {
                                button.example = btn.copy_code_example;
                            }
                            break;
                        case 'FLOW':
                            button.flow_id = String(btn.flow_id);
                            button.flow_action = btn.flow_action || 'navigate';
                            if (button.flow_action === 'navigate' && btn.navigate_screen) {
                                button.navigate_screen = String(btn.navigate_screen).toUpperCase();
                            }
                            break;
                        case 'CATALOG':
                            // Catalog buttons don't need additional properties
                            break;
                        case 'MPM':
                            // MPM buttons don't need additional properties  
                            break;
                        default:
                            // QUICK_REPLY and other types don't need additional properties
                            break;
                    }

                    return button;
                })
            };
            builtComponents.push(buttonComponent);
        }

        return builtComponents;
    };

    const renderComponentForm = (component) => {
        const variables = extractVariables(component.text || '');

        return (
            <Card
                key={component.id}
                size="small"
                hoverable
                title={
                    <Space>
                        <Badge status="processing" />
                        {component.type}
                        {variables.length > 0 && (
                            <Tag color="blue" size="small">{variables.length} variable(s)</Tag>
                        )}
                    </Space>
                }
                extra={
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeComponent(component.id)}
                        size="small"
                    />
                }
                className="component-card mb-2"
                bodyStyle={{ padding: '12px' }}
            >
                {component.type === 'HEADER' && (
                    <>
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={headerType === 'MEDIA' ? 8 : 12}>
                                <Form.Item label="Header Format" className="mb-2"
                                    help={`For a location-type header, you provide the actual location details (latitude and longitude) when sending the template message.`}
                                >
                                    <Select
                                        value={component.format}
                                        onChange={(value) => {
                                            updateComponent(component.id, 'format', value);
                                            setHeaderType(value);
                                            if (value !== 'MEDIA') {
                                                setSelectedMediaType(null);
                                            }
                                        }}

                                        className="w-100"
                                    >
                                        {headerFormats.map(format => (
                                            <Option key={format.value} value={format.value}>
                                                {format.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>

                            {component.format === 'TEXT' && (
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Header Text" className="mb-2">
                                        <Input
                                            value={component.text}
                                            onChange={(e) => handleHeaderChange(component.id, e.target.value)}
                                            placeholder="Enter header text (max 60 characters)"
                                            maxLength={60}
                                            showCount
                                            className="form-control"
                                            addonAfter={
                                                <Popover
                                                    content={
                                                        <EmojiPicker
                                                            onEmojiClick={(emojiObject) =>
                                                                handleEmojiSelect(emojiObject, component.id, component.text)
                                                            }
                                                            width={300}
                                                            height={350}
                                                        />
                                                    }
                                                    title="Choose an Emoji"
                                                    trigger="click"
                                                    placement="bottomRight"
                                                >
                                                    <Button
                                                        type="text"
                                                        icon={<SmileOutlined />}
                                                        size="small"
                                                    />
                                                </Popover>
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                            )}

                            {/* Media Header */}
                            {component.format === 'MEDIA' && (
                                <Col xs={24} sm={8}>
                                    <Form.Item label="Media Type:" className="mb-2">
                                        <Radio.Group
                                            value={selectedMediaType}
                                            onChange={(e) => setSelectedMediaType(e.target.value)}
                                        >
                                            <Radio.Button value="IMAGE">
                                                <FileImageOutlined /> Image
                                            </Radio.Button>
                                            <Radio.Button value="VIDEO">
                                                <PlaySquareOutlined /> Video
                                            </Radio.Button>
                                            <Radio.Button value="DOCUMENT">
                                                <FileTextOutlined /> Document
                                            </Radio.Button>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>
                            )}
                            {selectedMediaType && (
                                <>
                                    <Col xs={24} sm={4}>
                                        <Form.Item label="Upload Header Handle" className="mb-2" required>
                                            <Upload
                                                // beforeUpload={(file) => {
                                                //     uploadFile(file);
                                                //     return false;
                                                // }}
                                                beforeUpload={(file) => {
                                                    uploadFile(file);
                                                    // For preview
                                                    const reader = new FileReader();
                                                    reader.onload = (e) => {
                                                        setHeaderHandle({
                                                            url: e.target.result,
                                                            type: file.type,
                                                            name: file.name
                                                        });
                                                    };
                                                    reader.readAsDataURL(file);
                                                    return false;
                                                }}
                                                onRemove={() => {
                                                    setHeaderHandle(null);
                                                    setUploadDisabled(false);
                                                }}
                                                accept={
                                                    selectedMediaType === 'IMAGE' ? '.jpg,.jpeg,.png' :
                                                        selectedMediaType === 'VIDEO' ? '.mp4,.3gp' :
                                                            '.pdf'
                                                }
                                                maxCount={1}
                                                showUploadList={false}
                                            >
                                                <Button
                                                    icon={<UploadOutlined />}
                                                    disabled={uploadDisabled}
                                                    loading={loading}
                                                    size="medium"
                                                >
                                                    Select {selectedMediaType.toLowerCase()}
                                                </Button>
                                            </Upload>
                                        </Form.Item>
                                    </Col>
                                </>
                            )}
                        </Row>


                        {/* Location Header */}
                        {component.format === 'LOCATION' && (
                            <div className="my-2">
                                <Alert
                                    message="Location Header Selected"
                                    description="No configuration is required for location templates. When you use this template in campaigns or messages, you will be prompted to enter the specific location coordinates and details at that time."
                                    type="info"
                                    showIcon
                                />
                            </div>
                        )}
                    </>
                )}

                {component.type === 'BODY' && (
                    <div>
                        <Row gutter={[16, 8]}>
                            <Col xs={24} md={24}>
                                <Form.Item
                                    // label="Body Text"
                                    required
                                    help="Main message text that appears above the carousel. Use {{1}}, {{2}}, etc. for dynamic variables"
                                    className="mb-2"
                                >
                                    <Input.TextArea
                                        ref={bodyTextAreaRef}
                                        rows={3}
                                        placeholder="Enter your main message text... Use {{1}}, {{2}} for variables"
                                        value={component.text}
                                        onChange={(e) => handleBodyChange(component.id, e.target.value)}
                                        maxLength={1024}
                                        showCount
                                    />
                                    <div className="mt-2 d-flex gap-2 align-items-center">

                                        <Space size="small">
                                            <Button
                                                size="small"
                                                icon={<PlusOutlined />}
                                                onClick={() => addVariable(component.text || '', (newText) =>
                                                    updateComponent(component.id, 'text', newText)
                                                    , bodyTextAreaRef)}
                                            >
                                                Add Variable
                                            </Button>
                                            <Popover
                                                content={
                                                    <EmojiPicker
                                                        onEmojiClick={(emojiObject) =>
                                                            handleEmojiSelect(emojiObject, component.id, component.text)
                                                        }
                                                        width={300}
                                                        height={350}
                                                    />
                                                }
                                                title="Choose an Emoji"
                                                trigger="click"
                                                placement="bottomLeft"
                                            >
                                                <Button
                                                    size="small"
                                                    icon={<SmileOutlined />}
                                                >
                                                    Add Emoji
                                                </Button>
                                            </Popover>
                                            {extractVariables(component.text).length > 0 && (
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    Variables: {extractVariables(component.text).map(v => `{{${v}}}`).join(', ')}
                                                </Text>
                                            )}
                                        </Space>
                                    </div>
                                    {component.text && !validateVariableSequence(component.text) && (
                                        <Alert
                                            message="Variable Sequence Error"
                                            description="Variables must start from {{1}} and be sequential ({{1}}, {{2}}, {{3}}, etc.)"
                                            type="warning"
                                            showIcon
                                            size="small"
                                            style={{ marginTop: '8px' }}
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                )}

                {component.type === 'FOOTER' && (
                    <Form.Item label="Footer Text" className="mb-2">
                        <Input
                            value={component.text}
                            onChange={(e) => updateComponent(component.id, 'text', e.target.value)}
                            placeholder="Enter footer text (max 60 characters)"
                            maxLength={60}
                            showCount
                            size="small"
                            className="form-control"
                            addonAfter={
                                <Popover
                                    content={
                                        <EmojiPicker
                                            onEmojiClick={(emojiObject) =>
                                                handleEmojiSelect(emojiObject, component.id, component.text)
                                            }
                                            width={300}
                                            height={350}
                                        />
                                    }
                                    title="Choose an Emoji"
                                    trigger="click"
                                    placement="bottomRight"
                                >
                                    <Button
                                        type="text"
                                        icon={<SmileOutlined />}
                                        size="small"
                                    />
                                </Popover>
                            }
                        />
                    </Form.Item>
                )}
            </Card>
        );
    };

    const renderButtonForm = (button) => {
        const urlVariables = button.type === 'URL' ? extractVariables(button.url || '') : [];

        return (
            <Card
                key={button.id}
                size="small"
                hoverable
                title={
                    <Space>
                        <Badge status="success" />
                        {button.type} Button
                        {button.type === 'URL' && urlVariables.length > 0 && (
                            <Tag color={urlVariables.length > 1 ? "red" : "orange"}>
                                {urlVariables.length} URL variable(s)
                                {urlVariables.length > 1 && " (Max 1 allowed)"}
                            </Tag>
                        )}
                    </Space>
                }
                extra={
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeButton(button.id)}
                        className="btn-sm"
                    />
                }
                className="button-card mb-3"
            >
                <Row gutter={ROW_GUTTER}>
                    <Col xs={24} sm={24}>
                        <Form.Item label="Button Type">
                            <Select
                                value={button.type}
                                onChange={(value) => handleButtonTypeChange(button.id, value)}
                                className="w-100"
                            >
                                {buttonTypes.map(type => {
                                    const canSelect = canSelectButtonType(type.value, button.id);
                                    const currentCount = getButtonCounts()[type.value] || 0;
                                    const limit = BUTTON_LIMITS[type.value];

                                    return (
                                        <Option
                                            key={type.value}
                                            value={type.value}
                                            disabled={!canSelect}
                                        >
                                            {type.label}
                                            {!canSelect && (
                                                <span style={{ color: '#ff4d4f', marginLeft: '8px' }}>
                                                    ({currentCount}/{limit} limit reached)
                                                </span>
                                            )}
                                        </Option>
                                    );
                                })}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={button.type === 'URL' ? 8 : button.type === 'FLOW' ? (button.flow_action === 'navigate' ? 6 : 8) : 12}>
                        <Form.Item label="Button Text" required>
                            <Input
                                value={button.text}
                                onChange={(e) => updateButton(button.id, 'text', e.target.value)}
                                placeholder="Enter button text"
                                maxLength={25}
                                showCount
                                className="form-control"
                            />
                        </Form.Item>
                    </Col>
                    {button.type === 'PHONE_NUMBER' && (
                        <Col xs={24} sm={12}>
                            <Form.Item label="Phone Number (with country code)" required>
                                <Input
                                    value={button.phone_number}
                                    onChange={(e) => updateButton(button.id, 'phone_number', e.target.value)}
                                    placeholder="+1234567890"
                                    className="form-control"
                                />
                            </Form.Item>
                        </Col>
                    )}
                    {button.type === 'URL' && (
                        <>
                            <Col xs={24} sm={4}>
                                <Form.Item label="URL Type" >
                                    <Radio.Group
                                        value={button.url_type || 'STATIC'}
                                        onChange={(e) => updateButton(button.id, 'url_type', e.target.value)}
                                        className="w-100"
                                    >
                                        <Radio value="STATIC" className="me-3">Static URL</Radio>
                                        <Radio value="DYNAMIC">Dynamic URL</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label="URL"
                                    required
                                    validateStatus={
                                        button.url_type === 'DYNAMIC' && !validateUrlVariables(button.url)
                                            ? 'error'
                                            : ''
                                    }
                                    help={
                                        button.url_type === 'DYNAMIC' && !validateUrlVariables(button.url)
                                            ? 'URL buttons can only contain 1 variable maximum. Please use {{1}} only.'
                                            : null
                                    }
                                >
                                    <Input
                                        value={button.url}
                                        onChange={(e) => updateButton(button.id, 'url', e.target.value)}
                                        placeholder={
                                            button.url_type === 'DYNAMIC'
                                                ? 'https://example.com/{{1}}'
                                                : 'https://example.com'
                                        }
                                        className="form-control"
                                        status={
                                            button.url_type === 'DYNAMIC' && !validateUrlVariables(button.url)
                                                ? 'error'
                                                : ''
                                        }
                                    />
                                    <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                        <div className="text-muted small">
                                            {(button.url || '').length}/2000 characters
                                        </div>
                                        {button.url_type === 'DYNAMIC' && (
                                            <Space size="small">
                                                <Button
                                                    size="small"
                                                    icon={<PlusOutlined />}
                                                    onClick={() => addVariableToUrl(button.url, button.id)}
                                                    disabled={extractVariables(button.url || '').length >= 1}
                                                >
                                                    Add Variable
                                                </Button>
                                                {extractVariables(button.url || '').length > 0 && (
                                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                                        Variables: {extractVariables(button.url || '').map(v => `{{${v}}}`).join(', ')}
                                                    </Text>
                                                )}
                                            </Space>
                                        )}
                                    </div>
                                </Form.Item>
                            </Col>
                        </>
                    )}

                    {button.type === 'COPY_CODE' && (
                        <Col xs={24} sm={12}>
                            <Form.Item label="Copy Code Example">
                                <Input
                                    value={button.copy_code_example}
                                    onChange={(e) => updateButton(button.id, 'copy_code_example', e.target.value)}
                                    placeholder="SAVE20 (example coupon code)"
                                    className="form-control"
                                />
                            </Form.Item>
                        </Col>
                    )}

                    {button.type === 'FLOW' && (
                        <>
                            <Col xs={24} sm={button.flow_action === 'navigate' ? 6 : 8}>
                                <Form.Item
                                    label="MetaFlow"
                                    required
                                    help={
                                        flows.length === 0 && !flowsLoading
                                            ? 'No published MetaFlows found. Publish a flow first.'
                                            : 'Only published MetaFlows can be attached to templates.'
                                    }
                                >
                                    <Select
                                        value={button.flow_id || undefined}
                                        onChange={(value) => handleFlowSelect(button.id, value)}
                                        placeholder="Select published MetaFlow"
                                        loading={flowsLoading}
                                        showSearch
                                        optionFilterProp="children"
                                        className="w-100"
                                    >
                                        {flows.map((flow) => (
                                            <Option
                                                key={flow.meta_flow_id}
                                                value={String(flow.meta_flow_id)}
                                            >
                                                {flow.name}
                                                {flow.is_dynamic ? ' (dynamic)' : ' (static)'}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={button.flow_action === 'navigate' ? 6 : 8}>
                                <Form.Item
                                    label="Flow action"
                                    required
                                    help={
                                        button.flow_action === 'data_exchange'
                                            ? 'Opens the flow and calls your endpoint on INIT so the first screen can load dynamic data (lists, options, etc.). Use this for dynamic MetaFlows.'
                                            : 'Opens the flow on the selected start screen with no endpoint call. Use this for static MetaFlows, or when the first screen does not need API data.'
                                    }
                                >
                                    <Select
                                        value={button.flow_action || 'navigate'}
                                        onChange={(value) =>
                                            handleFlowActionChange(button.id, value, button.flow_id)
                                        }
                                        className="w-100"
                                        disabled={!button.flow_id}
                                    >
                                        {flowActions.map((action) => (
                                            <Option key={action.value} value={action.value}>
                                                {action.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>

                            {button.flow_action === 'navigate' && (
                                <Col xs={24} sm={6}>
                                    <Form.Item label="Start screen" required>
                                        <Select
                                            value={button.navigate_screen || undefined}
                                            onChange={(value) =>
                                                updateButton(button.id, 'navigate_screen', value)
                                            }
                                            placeholder="Select screen"
                                            loading={flowScreensLoadingId === button.flow_id}
                                            className="w-100"
                                            disabled={!button.flow_id}
                                        >
                                            {(flowScreensById[button.flow_id] || []).map((screen) => (
                                                <Option key={screen.id} value={screen.id}>
                                                    {screen.title} ({screen.id})
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            )}
                        </>
                    )}

                    {(button.type === 'CATALOG' || button.type === 'MPM') && (
                        <Alert
                            message={`${button.type} Button`}
                            description={`${button.type} buttons don't require additional configuration. They will use your business catalog.`}
                            type="info"
                            showIcon
                        />
                    )}
                </Row>
            </Card>
        );
    };
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const handleSubmit = async (values) => {

        // Get current form values multiple ways
        const currentFormValues = form.getFieldsValue();

        const specificValues = {
            name: form.getFieldValue('name'),
            category: form.getFieldValue('category'),
            language: form.getFieldValue('language'),
            allow_category_change: form.getFieldValue('allow_category_change')
        };

        // Try to get values from form fields directly
        try {
            await form.validateFields();
        } catch (errorInfo) {
            message.error('Please fix validation errors before submitting');
        }

        const finalValues = { ...currentFormValues, ...values, ...specificValues };

        // Validate button counts before submission
        const buttonErrors = validateButtonCounts();
        if (buttonErrors.length > 0) {
            message.error('Please fix button validation errors before submitting');
            return;
        }

        // Validate that we have at least a body component
        const hasBody = components.some(comp => comp.type === 'BODY' && comp.text?.trim());
        if (!hasBody) {
            message.error('Template must have a body component with text');
            return;
        }

        // Validate media upload for media headers
        const headerComponent = components.find(comp => comp.type === 'HEADER');
        if (headerComponent && headerComponent.format === 'MEDIA' && !headerHandle) {
            message.error('Please upload media for your header component');
            return;
        }

        setLoading(true);
        setSubmitError(null);
        try {
            // Build template object according to Meta's API specification
            const templateObject = {
                name: finalValues.name || 'undefined_template_name',
                language: finalValues.language || 'en_US',
                category: finalValues.category || 'UTILITY',
                allow_category_change: finalValues.allow_category_change || false,
                components: buildComponents()
            };

            setShowSubmitModal(true);
            await apiClient.post('messaging/templates', templateObject);
            message.success('Template submitted successfully to Meta for review!');
            // setTemplateData(summary);
            // setPreviewVisible(true);
            navigate(-1);

        } catch (error) {
            setSubmitError(ErrorExtractor(error));
        } finally {
            setShowSubmitModal(false);
            setLoading(false);
        }
    };



    const steps = [
        {
            title: 'Basic Info',
            content: 'Template basic information'
        },
        {
            title: 'Components',
            content: 'Add template components'
        },
        {
            title: 'Buttons',
            content: 'Configure action buttons'
        },
        {
            title: 'Review',
            content: 'Review and submit'
        }
    ];

    // Validate current step before proceeding
    const validateCurrentStep = async () => {
        try {
            if (currentStep === 0) {
                // Validate basic info step
                await form.validateFields(['name', 'language', 'category']);
                const values = form.getFieldsValue();
                // also check name dosent exist
                const nameExists = templates.some(template => template.name === values.name);
                if (nameExists) {
                    form.setFields([
                        {
                            name: 'name',
                            errors: ['Template name already exists']
                        }
                    ]);
                    return false;
                }
                return true;
            } else if (currentStep === 1) {
                // Validate components step - must have at least a body component
                const hasBody = components.some(comp => comp.type === 'BODY' && comp.text?.trim());
                if (!hasBody) {
                    message.error('Please add a Body component with text content');
                    return false;
                }

                // Validate media upload for media headers
                const headerComponent = components.find(comp => comp.type === 'HEADER');
                if (headerComponent && headerComponent.format === 'MEDIA' && !headerHandle) {
                    message.error('Please upload media for your header component');
                    return false;
                } return true;
            } else if (currentStep === 2) {
                // Validate buttons step
                const buttonErrors = validateButtonCounts();
                if (buttonErrors.length > 0) {
                    message.error('Please fix button validation errors before proceeding');
                    return false;
                }
                return true;
            }
            return true;
        } catch (error) {
            // Form validation failed
            return false;
        }
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <>

                        <Row gutter={24}>
                            <Col span={12}>
                                <Form.Item
                                    name="name"
                                    label={<span>Template Name</span>}
                                    rules={[
                                        { required: true, message: 'Please enter template name' },
                                        { pattern: /^[a-z0-9_]+$/, message: 'Only lowercase letters, numbers, and underscores allowed' }
                                    ]}
                                    hasFeedback
                                >
                                    <Input placeholder="e.g., welcome_message" showCount maxLength={512} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="language"
                                    label={'Language'}
                                    rules={[{ required: true, message: 'Please select a language' }]}
                                    hasFeedback
                                >
                                    <Select
                                        showSearch
                                        placeholder="Select language"
                                        optionFilterProp="children"
                                        filterOption={(input, option) => {
                                            const lang = supportedLanguages.find(l => l.code === option.value);
                                            if (!lang) return false;
                                            const searchText = `${lang.name} ${lang.code}`.toLowerCase();
                                            return searchText.indexOf(input.toLowerCase()) >= 0;
                                        }}
                                    >
                                        {supportedLanguages.map(lang => (
                                            <Option key={lang.code} value={lang.code}>
                                                {lang.name} ({lang.code})
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="category"
                                    label={<span>Category <span style={{ color: 'red' }}>*</span></span>}
                                    rules={[{ required: true, message: 'Please select a category' }]}
                                    hasFeedback
                                >
                                    <Select>
                                        {templateCategories.map(cat => (
                                            <Option key={cat.value} value={cat.value}>
                                                <div>
                                                    <div>{cat.label}</div>
                                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                                        {cat.description}
                                                    </Text>
                                                </div>
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="allow_category_change"
                                    label="Allow Category Change"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                ); case 1:
                return (
                    <div>
                        <Row gutter={[12, 12]} className='mb-2'>
                            <Col span={12}>
                                <Title level={4} className="mb-2 mb-md-0" style={{ textAlign: 'left', width: '100%' }}>
                                    Template Components
                                </Title>
                            </Col>
                            <Col span={12}>
                                <Row gutter={ROW_GUTTER}>

                                    {componentTypes.slice(0, 3).map(type => (
                                        <Col span={8} key={type.value}>
                                            <Button
                                                type="dashed"
                                                icon={<PlusOutlined />}
                                                onClick={() => addComponent(type.value)}
                                                disabled={components.some(c => c.type === type.value)}
                                                block
                                            >
                                                Add {type.label}
                                            </Button>
                                        </Col>
                                    ))}
                                </Row>
                            </Col>
                        </Row>

                        <Alert
                            message="Component Guidelines"
                            description={
                                <ul className="mb-0">
                                    <li>Header is optional and can be text, image, video, document, or location</li>
                                    <li>Body is required and contains the main message content</li>
                                    <li>Footer is optional and limited to 60 characters</li>
                                    <li>Use {`{{1}}, {{2}}, etc. for parameters in the text`}</li>
                                </ul>
                            }
                            type="info"
                            showIcon
                            closable
                            className="mb-4"
                        />

                        {components.map(component => renderComponentForm(component))}

                        {components.length === 0 && (
                            <Card className="text-center py-5">
                                <Text type="secondary">No components added yet. Start by adding a Body component.</Text>
                            </Card>
                        )}
                    </div>
                );

            case 2:
                const buttonValidationErrors = validateButtonCounts();
                return (
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <Title level={4}>Action Buttons</Title>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={addButton}
                                disabled={buttons.length >= BUTTON_LIMITS.TOTAL}
                            >
                                Add Button ({buttons.length}/{BUTTON_LIMITS.TOTAL})
                            </Button>
                        </div>

                        {buttonValidationErrors.length > 0 && (
                            <Alert
                                message="Button Validation Errors"
                                description={
                                    <ul className="mb-0">
                                        {buttonValidationErrors.map((error, index) => (
                                            <li key={index}>{error}</li>
                                        ))}
                                    </ul>
                                }
                                type="error"
                                showIcon
                                className="mb-4"
                            />
                        )}

                        {buttons.length > 0 && (
                            <Alert
                                banner
                                message={
                                    <>
                                        <strong>Current Button Distribution:</strong>
                                        <span style={{ marginLeft: 8 }}>
                                            You have {buttons.length} of {BUTTON_LIMITS.TOTAL} buttons: {getButtonCountSummary().join(', ')}
                                        </span>
                                    </>
                                }
                                type="info"
                                closable
                                showIcon
                                style={{ marginBottom: 8 }}
                            />
                        )}

                        <Alert
                            banner
                            message={<strong>Button Guidelines & Limits</strong>}
                            description={
                                <div >
                                    <div className='d-flex align-items-center gap-5'>
                                        <div>
                                            {/* <strong>Button Limits:</strong> */}
                                            <ul style={{ margin: '4px 0', paddingLeft: 16 }}>
                                                <li>Quick Reply: Max {BUTTON_LIMITS.QUICK_REPLY}</li>
                                                <li>URL: Max {BUTTON_LIMITS.URL}</li>
                                                <li>Phone: Max {BUTTON_LIMITS.PHONE_NUMBER}</li>
                                                <li>Copy Code: Max {BUTTON_LIMITS.COPY_CODE}</li>
                                            </ul>
                                        </div>
                                        <div>
                                            {/* <strong>Advanced Buttons:</strong> */}
                                            <ul style={{ margin: '4px 0', paddingLeft: 16 }}>
                                                <li>Flow: Max {BUTTON_LIMITS.FLOW}</li>
                                                <li>Catalog: Max {BUTTON_LIMITS.CATALOG}</li>
                                                <li>MPM: Max {BUTTON_LIMITS.MPM}</li>
                                                <li>Total: Max {BUTTON_LIMITS.TOTAL}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            }
                            closable
                            type="info"
                            showIcon
                        />


                        {buttons.map(button => renderButtonForm(button))}

                        {buttons.length === 0 && (
                            <Card className="text-center py-5">
                                <Text type="secondary">No buttons added yet. Buttons are optional but can enhance user interaction.</Text>
                            </Card>
                        )}
                    </div>
                );

            case 3:
                return (
                    <div>
                        <Title level={4}>Review Template</Title>
                        <Card>
                            <Descriptions title="Template Information" bordered>
                                <Descriptions.Item label="Name" span={2}>
                                    {form.getFieldValue('name')}
                                </Descriptions.Item>
                                <Descriptions.Item label="Language">
                                    {supportedLanguages.find(l => l.code === form.getFieldValue('language'))?.name}
                                </Descriptions.Item>
                                <Descriptions.Item label="Category" span={2}>
                                    {templateCategories.find(c => c.value === form.getFieldValue('category'))?.label}
                                </Descriptions.Item>
                                <Descriptions.Item label="Allow Category Change">
                                    {form.getFieldValue('allow_category_change') ? 'Yes' : 'No'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Components" span={3}>
                                    {components.length} component(s): {components.map(c => c.type).join(', ')}
                                </Descriptions.Item>
                                <Descriptions.Item label="Buttons" span={3}>
                                    {buttons.length} button(s): {buttons.map(b => b.type).join(', ')}
                                </Descriptions.Item>
                                {buttons
                                    .filter((b) => b.type === 'FLOW')
                                    .map((b, index) => {
                                        const flow = flows.find(
                                            (f) => String(f.meta_flow_id) === String(b.flow_id)
                                        );
                                        return (
                                            <Descriptions.Item
                                                key={`flow-${index}`}
                                                label={`Flow button: ${b.text || 'Untitled'}`}
                                                span={3}
                                            >
                                                {flow?.name || b.flow_id || '—'}
                                                {' · '}
                                                {b.flow_action || 'navigate'}
                                                {b.flow_action === 'navigate' && b.navigate_screen
                                                    ? ` · screen ${b.navigate_screen}`
                                                    : ''}
                                            </Descriptions.Item>
                                        );
                                    })}
                            </Descriptions>
                        </Card>

                        {submitError && (
                            <Alert
                                message="Submission Error"
                                description={submitError}
                                type="error"
                                showIcon
                                closable
                                onClose={() => setSubmitError(null)}
                                className="mt-3 animate-fade-in"
                            />
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <Card
            title={
                <Space align="center">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate(-1)}
                    />
                    <span style={{ fontSize: '18px', fontWeight: 600 }}>Create Template</span>
                </Space>
            }
        >
            <div className="mb-4">
                <Paragraph type="secondary" className="lead">
                    Create and submit WhatsApp Business API message templates to Meta for approval.
                    Templates must follow Meta's guidelines and will be reviewed before activation.
                </Paragraph>
            </div>

            <Steps
                current={currentStep}
                className="mb-4"
                items={steps.map(item => ({
                    key: item.title,
                    title: item.title
                }))}
            />

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                autoComplete="off"
                initialValues={{
                    category: 'UTILITY',
                    language: 'en_US',
                    allow_category_change: false
                }}
            >
                <div className="step-content">
                    {renderStepContent(currentStep)}
                </div>

                <Divider />

                <div className="template-actions d-flex justify-content-between">
                    <div>
                        {currentStep > 0 && (
                            <Button
                                onClick={() => setCurrentStep(currentStep - 1)}
                                className="me-2"
                            >
                                Previous
                            </Button>
                        )}
                    </div>

                    <Space size="middle">
                        {/* <Button
                            icon={<EyeOutlined />}
                            onClick={() => {
                                const values = form.getFieldsValue();
                                const templateObject = {
                                    name: values.name || 'preview_template',
                                    language: values.language || 'en_US',
                                    category: values.category || 'UTILITY',
                                    allow_category_change: values.allow_category_change || false,
                                    components: buildComponents()
                                };
                                setTemplateData(templateObject);
                                setPreviewVisible(true);
                            }}
                            className="btn-outline-info"
                        >
                            Preview
                        </Button>

                        <Button
                            icon={<SaveOutlined />}
                            onClick={() => message.info('Template saved as draft')}
                            className="btn-outline-secondary"
                        >
                            Save Draft
                        </Button> */}

                        {currentStep < steps.length - 1 ? (
                            <Button
                                onClick={async () => {
                                    const isValid = await validateCurrentStep();
                                    if (isValid) {
                                        setCurrentStep(currentStep + 1);
                                    }
                                }}
                                type="primary"
                            >
                                Next
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                // htmlType="submit"
                                loading={loading}
                                onClick={async () => {
                                    const isValid = await validateCurrentStep();
                                    if (isValid) {
                                        form.submit(); // Only submit on last step
                                    }
                                }}
                                icon={<SendOutlined />}
                                className="btn-lg btn-success"
                            >
                                Submit Template
                            </Button>
                        )}
                    </Space>
                </div>
            </Form>

            <Modal
                title={
                    <div className="d-flex align-items-center">
                        <span className="me-2">📱</span>
                        Template Preview
                    </div>
                }
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setPreviewVisible(false)}>
                        Close
                    </Button>,
                    <Button key="copy" icon={<SaveOutlined />} onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(templateData, null, 2));
                        message.success('Template JSON copied to clipboard');
                    }}>
                        Copy JSON
                    </Button>
                ]}
                width={900}
                className="template-preview-modal"
            >
                <Row gutter={24}>
                    <Col xs={24} md={12}>
                        <div className="preview-phone">
                            <div className="preview-screen">
                                <div className="whatsapp-header">
                                    <strong>WhatsApp Business</strong>
                                </div>

                                <div className="whatsapp-message-bubble">
                                    {components.map((comp, index) => (
                                        <div key={index} className="mb-2">
                                            {comp.type === 'HEADER' && (
                                                <div className="fw-bold text-primary">
                                                    {comp.format === 'TEXT' ? comp.text : `[${comp.format}]`}
                                                </div>
                                            )}
                                            {comp.type === 'BODY' && (
                                                <div className="mb-1">{comp.text}</div>
                                            )}
                                            {comp.type === 'FOOTER' && (
                                                <div className="text-muted small">{comp.text}</div>
                                            )}
                                        </div>
                                    ))}

                                    {buttons.length > 0 && (
                                        <div className="mt-3 d-flex flex-column gap-1">
                                            {buttons.map((btn, index) => (
                                                <Button
                                                    key={index}
                                                    size="small"
                                                    block
                                                    className={`btn-${btn.type.toLowerCase().replace('_', '-')}`}
                                                >
                                                    {btn.type === 'PHONE_NUMBER' && '📞 '}
                                                    {btn.type === 'URL' && '🔗 '}
                                                    {btn.type === 'COPY_CODE' && '📋 '}
                                                    {btn.type === 'FLOW' && '🔄 '}
                                                    {btn.text}
                                                </Button>
                                            ))}
                                        </div>
                                    )}

                                    <div className="message-time">
                                        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>

                    <Col xs={24} md={12}>
                        <Card title="Template Statistics" className="mb-3">
                            {templateData.stats && (
                                <Descriptions size="small" column={1}>
                                    <Descriptions.Item label="Body Variables">
                                        {templateData.stats.bodyVariables}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Total Buttons">
                                        {templateData.stats.totalButtons}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Button Types">
                                        {Object.entries(templateData.stats.buttonTypes || {}).map(([type, count]) => (
                                            <Tag key={type} color="blue" className="me-1">
                                                {type}: {count}
                                            </Tag>
                                        ))}
                                    </Descriptions.Item>
                                </Descriptions>
                            )}
                        </Card>

                        <Card title="Meta API JSON" size="small">
                            <div className="json-preview">
                                {JSON.stringify(templateData.template || templateData, null, 2)}
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Modal>

            <Modal
                open={showSubmitModal}
                footer={null}
                closable={false}
                centered
                width={400}
                bodyStyle={{ textAlign: 'center', padding: 40 }}
            >
                <LoadingOutlined style={{ fontSize: 48, color: PRIMARY }} spin />
                <div style={{ marginTop: 24, fontSize: 18, fontWeight: 500 }}>
                    Submitting your template...
                </div>
                <div style={{ marginTop: 8, color: '#888' }}>
                    Please wait while we send your template to Meta for review.
                </div>
            </Modal>
        </Card>
    );
};

export default NewTemplate;
